import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Send } from 'lucide-react';
import { Buton, Etiket, Girdi, Iskelet, Kart, KartIcerik, toast } from '@emanetx/ui';
import { smsAyar, type NetgsmAyar } from '@emanetx/sdk';
import { istemci } from '../istemci.js';

interface NetgsmForm {
  apiUrl: string;
  kullaniciAdi: string;
  sifre: string;
  baslik: string;
}

const BOS: NetgsmForm = {
  apiUrl: 'https://api.netgsm.com.tr/sms/send/xml',
  kullaniciAdi: '',
  sifre: '',
  baslik: '',
};

export function SayfaNetgsm() {
  const qc = useQueryClient();
  const [form, setForm] = useState<NetgsmForm>(BOS);
  const [testTelefon, setTestTelefon] = useState('+90');
  const [testMesaj, setTestMesaj] = useState(
    'EmanetX NetGSM test — bu mesaji aldiysan entegrasyon calisiyor.',
  );

  const ayarSorgu = useQuery({
    queryKey: ['sms-ayar', 'netgsm'],
    queryFn: () => smsAyar.netgsmGetir(istemci),
  });

  useEffect(() => {
    if (ayarSorgu.data) {
      setForm({
        apiUrl: ayarSorgu.data.apiUrl,
        kullaniciAdi: ayarSorgu.data.kullaniciAdi,
        sifre: '',
        baslik: ayarSorgu.data.baslik,
      });
    }
  }, [ayarSorgu.data]);

  const kaydet = useMutation({
    mutationFn: () => {
      const giris: Partial<NetgsmAyar> = {
        apiUrl: form.apiUrl,
        kullaniciAdi: form.kullaniciAdi,
        baslik: form.baslik,
      };
      if (form.sifre.length > 0) giris.sifre = form.sifre;
      return smsAyar.netgsmKaydet(istemci, giris);
    },
    onSuccess: () => {
      toast.success('NetGSM ayarları kaydedildi');
      setForm((f) => ({ ...f, sifre: '' }));
      void qc.invalidateQueries({ queryKey: ['sms-ayar', 'netgsm'] });
    },
    onError: (err: unknown) => toast.error(hataMesaji(err)),
  });

  const test = useMutation({
    mutationFn: () => smsAyar.netgsmTest(istemci, { telefon: testTelefon, mesaj: testMesaj }),
    onSuccess: (sonuc) => {
      if (sonuc.basarili) {
        toast.success(`SMS gönderildi: ${sonuc.detay ?? ''}`);
      } else {
        toast.error(`Gönderilemedi: ${sonuc.mesaj} — ${sonuc.detay ?? ''}`);
      }
    },
    onError: (err: unknown) => toast.error(hataMesaji(err)),
  });

  if (ayarSorgu.isLoading) return <Iskelet className="h-80" />;

  const formGecerli =
    form.apiUrl.includes('http') &&
    form.kullaniciAdi.length > 0 &&
    form.baslik.length > 0 &&
    (ayarSorgu.data?.yapilandirildi || form.sifre.length > 0);

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">NetGSM SMS</h1>
        <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
          Mobil giriş doğrulama, kiralama hatırlatma ve sistem mesajları bu sağlayıcıdan gider.
          Yapılandırma yapılmadığında lokal'de mock log, canlıda hata fırlatılır.
        </p>
      </header>

      <Kart>
        <KartIcerik className="space-y-4">
          <div>
            <Etiket>API URL</Etiket>
            <Girdi
              value={form.apiUrl}
              onChange={(e) => setForm({ ...form, apiUrl: e.target.value })}
              placeholder="https://api.netgsm.com.tr/sms/send/xml"
            />
            <p className="text-fume-400 mt-1 text-xs">
              NetGSM XML SMS gönderim uç noktası — varsayılan olarak doğru.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Etiket>Kullanıcı Adı</Etiket>
              <Girdi
                value={form.kullaniciAdi}
                onChange={(e) => setForm({ ...form, kullaniciAdi: e.target.value })}
                placeholder="850XXXXXXX"
              />
            </div>
            <div>
              <Etiket>
                Şifre {ayarSorgu.data?.yapilandirildi ? '(değiştirmek istemiyorsan boş bırak)' : ''}
              </Etiket>
              <Girdi
                type="password"
                value={form.sifre}
                onChange={(e) => setForm({ ...form, sifre: e.target.value })}
                placeholder={ayarSorgu.data?.yapilandirildi ? '••••••••' : 'NetGSM API şifresi'}
              />
            </div>
            <div className="sm:col-span-2">
              <Etiket>Gönderici Başlığı (msgheader)</Etiket>
              <Girdi
                value={form.baslik}
                onChange={(e) => setForm({ ...form, baslik: e.target.value.toUpperCase() })}
                placeholder="EMANETX"
                maxLength={11}
              />
              <p className="text-fume-400 mt-1 text-xs">
                NetGSM'de onaylı başlığın — maks 11 karakter, büyük harf önerilir.
              </p>
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Buton onClick={() => kaydet.mutate()} disabled={!formGecerli || kaydet.isPending}>
              {kaydet.isPending ? 'Kaydediliyor...' : 'Kaydet'}
            </Buton>
          </div>
        </KartIcerik>
      </Kart>

      <Kart>
        <KartIcerik className="space-y-3">
          <div className="flex items-center gap-2">
            <MessageSquare size={18} className="text-marka-600" />
            <h2 className="font-semibold">Test SMS Gönder</h2>
          </div>
          <p className="text-fume-500 text-sm">
            Kaydedilen ayarlarla gerçek bir SMS gönderir. NetGSM dakikadaki SMS limitini unutma —
            testi sınırlı tut.
          </p>
          <div className="grid gap-2 sm:grid-cols-[200px_1fr_auto]">
            <Girdi
              value={testTelefon}
              onChange={(e) => setTestTelefon(e.target.value)}
              placeholder="+90 5XX XXX XX XX"
            />
            <Girdi
              value={testMesaj}
              onChange={(e) => setTestMesaj(e.target.value)}
              maxLength={160}
            />
            <Buton
              stil="cizgili"
              onClick={() => test.mutate()}
              disabled={!testTelefon.startsWith('+') || testMesaj.length < 3 || test.isPending}
            >
              <Send size={14} /> {test.isPending ? '...' : 'Gönder'}
            </Buton>
          </div>
        </KartIcerik>
      </Kart>
    </div>
  );
}

function hataMesaji(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    return String((err as { message: string }).message);
  }
  return 'Bir hata oluştu';
}
